import type {
    AiqmSentimentAggregate,
    SentimentComparison,
    SentimentGranularity,
    SentimentMover,
    SentimentTheme,
    SentimentTrendBucket,
} from "@type/aiqm";
import {
    MAX_SENTIMENT_THEMES,
    MAX_SENTIMENT_TREND_BUCKETS,
    MAX_THEME_EXAMPLE_CALLS,
    netSentimentToLevel,
} from "@type/aiqm";
import axios from "axios";
import { getCookie } from "@util/cookie";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

// Build-time switch: when "true", the service returns deterministic mock data
// instead of hitting the (not-yet-built) backend endpoint. This is DISTINCT from
// any user-facing feature flag that controls whether the Insights tab is visible
// — keeping them separate lets us demo on mock data in one env while pointing at
// the real (empty) endpoint in another.
const USE_MOCK = import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true";

// Sentinel used by the Insights tag filter to represent "calls with no tags".
// It travels inside the same string[] as real tags; the query builder strips it
// out and flips the include_untagged flag instead (the backend can't express
// "untagged" through the tag-name OR filter). See
// BE_HANDOFF_AIQM_INSIGHTS_UNTAGGED_FILTER.
export const UNTAGGED_TAG = "__untagged__";

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
}

export interface SentimentAggregateParams {
    granularity?: SentimentGranularity; // default "weekly"
    start_date?: string; // ISO date; default = end_date - 90d
    end_date?: string; // ISO date; default = today
    campaign_id?: string;
    // Filter the aggregate to calls carrying ANY of these tags (OR semantics).
    // Tags are applied at upload time (see uploadAudioFiles). BACKEND: not yet
    // implemented — see BE_HANDOFF_AIQM_CALL_TAGS.md.
    tags?: string[];
}

// Result of fetching the current window plus the immediately-preceding window of
// the same length, so the UI can show period-over-period deltas + biggest movers
// without any new backend field — it just queries the same endpoint twice.
export interface SentimentAggregateComparison {
    current: AiqmSentimentAggregate;
    previous: AiqmSentimentAggregate | null;
    // Period-over-period figures (deltas + movers). Pre-computed by the BE when
    // compare=true; null when there's no previous window. During BE rollout the
    // service computes it locally from current/previous as a fallback.
    comparison: SentimentComparison | null;
}

// Given an inclusive [start, end] date window, return the equal-length window
// immediately before it. Pure string/date math (UTC) so it's deterministic.
export function previousPeriodRange(
    startDate: string,
    endDate: string
): { start_date: string; end_date: string } | null {
    const start = new Date(`${startDate}T00:00:00Z`);
    const end = new Date(`${endDate}T00:00:00Z`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    const dayMs = 86_400_000;
    const lengthDays = Math.round((end.getTime() - start.getTime()) / dayMs) + 1;
    const prevEnd = new Date(start.getTime() - dayMs);
    const prevStart = new Date(prevEnd.getTime() - (lengthDays - 1) * dayMs);
    return {
        start_date: prevStart.toISOString().slice(0, 10),
        end_date: prevEnd.toISOString().slice(0, 10),
    };
}

// Defensive normalizer: trusts the contract but clamps array sizes and coerces
// number fields so a misbehaving/early backend can't hang or NaN-out the render.
function normalizeAggregate(raw: unknown): AiqmSentimentAggregate {
    const r = (raw ?? {}) as Record<string, unknown>;
    const clampPct = (n: unknown): number => {
        const v = typeof n === "number" && Number.isFinite(n) ? n : 0;
        return Math.min(100, Math.max(0, v));
    };
    const clampNet = (n: unknown): number => {
        const v = typeof n === "number" && Number.isFinite(n) ? n : 0;
        return Math.min(100, Math.max(-100, v));
    };
    const breakdown = (b: unknown) => {
        const bb = (b ?? {}) as Record<string, unknown>;
        return {
            positive: clampPct(bb.positive),
            neutral: clampPct(bb.neutral),
            negative: clampPct(bb.negative),
        };
    };
    // Parse an optional BE-provided sentiment level enum; undefined when absent
    // so the accessors fall back to deriving it from the numeric score.
    const parseLevel = (
        v: unknown
    ): "positive" | "neutral" | "negative" | undefined =>
        v === "positive" || v === "neutral" || v === "negative" ? v : undefined;
    const optNumber = (v: unknown): number | undefined =>
        typeof v === "number" && Number.isFinite(v) ? v : undefined;

    const rawTrend = Array.isArray(r.trend) ? r.trend : [];
    const trend: SentimentTrendBucket[] = rawTrend
        .slice(0, MAX_SENTIMENT_TREND_BUCKETS)
        .map((t) => {
            const tt = (t ?? {}) as Record<string, unknown>;
            return {
                bucket_start: typeof tt.bucket_start === "string" ? tt.bucket_start : "",
                net_sentiment: clampNet(tt.net_sentiment),
                call_count:
                    typeof tt.call_count === "number" && tt.call_count >= 0
                        ? Math.round(tt.call_count)
                        : 0,
            };
        });

    const rawThemes = Array.isArray(r.themes) ? r.themes : [];
    const themes: SentimentTheme[] = rawThemes
        .slice(0, MAX_SENTIMENT_THEMES)
        .map((t, i) => {
            const tt = (t ?? {}) as Record<string, unknown>;
            return {
                id: typeof tt.id === "string" ? tt.id : `theme-${i}`,
                label: typeof tt.label === "string" ? tt.label : "Untitled theme",
                call_count:
                    typeof tt.call_count === "number" && tt.call_count >= 0
                        ? Math.round(tt.call_count)
                        : 0,
                avg_sentiment: clampNet(tt.avg_sentiment),
                breakdown: breakdown(tt.breakdown),
                example_call_ids: Array.isArray(tt.example_call_ids)
                    ? tt.example_call_ids
                          .filter((x): x is string => typeof x === "string")
                          .slice(0, MAX_THEME_EXAMPLE_CALLS)
                    : [],
                // Pre-computed by BE (defensive default if a field is missing).
                call_pct: optNumber(tt.call_pct) ?? 0,
                sentiment_level: parseLevel(tt.sentiment_level) ?? "neutral",
            };
        });

    const granularity = (["daily", "weekly", "monthly"] as const).includes(
        r.granularity as SentimentGranularity
    )
        ? (r.granularity as SentimentGranularity)
        : "weekly";

    return {
        granularity,
        start_date: typeof r.start_date === "string" ? r.start_date : "",
        end_date: typeof r.end_date === "string" ? r.end_date : "",
        total_calls:
            typeof r.total_calls === "number" && r.total_calls >= 0
                ? Math.round(r.total_calls)
                : 0,
        overall_net_sentiment: clampNet(r.overall_net_sentiment),
        overall_breakdown: breakdown(r.overall_breakdown),
        trend,
        themes,
        // Pre-computed by BE (defensive default if a field is missing).
        net_sentiment_level: parseLevel(r.net_sentiment_level) ?? "neutral",
        momentum: optNumber(r.momentum) ?? 0,
        theme_split: (() => {
            const ts = (r.theme_split ?? {}) as Record<string, unknown>;
            return {
                positive: optNumber(ts.positive) ?? 0,
                negative: optNumber(ts.negative) ?? 0,
            };
        })(),
    };
}

// Fetch the aggregated sentiment + themes for the authenticated client.
//
// Real endpoint (when built): GET /training/aiqm/insights/sentiment
//   - client scope derived server-side from auth (no client_id passed)
//   - returns a pre-aggregated, capped payload (see MAX_* constants)
//
// Until then, VITE_AIQM_INSIGHTS_MOCK=true returns deterministic fixture data.
// Shared query builder for the sentiment endpoint: granularity, date window,
// campaign, and the tag filter (real tags as repeated ?tags=, plus the
// include_untagged flag derived from the UNTAGGED_TAG sentinel).
function buildSentimentQuery(params: SentimentAggregateParams): URLSearchParams {
    const query = new URLSearchParams();
    query.set("granularity", params.granularity ?? "weekly");
    if (params.start_date) query.set("start_date", params.start_date);
    if (params.end_date) query.set("end_date", params.end_date);
    if (params.campaign_id) query.set("campaign_id", params.campaign_id);
    // Repeat the param per tag: ?tags=a&tags=b (BE reads as a list). The filter
    // can also include the UNTAGGED_TAG sentinel for "calls with no tags" — strip
    // it from the tag list and signal it via include_untagged instead.
    if (params.tags && params.tags.length > 0) {
        const realTags = params.tags.filter((t) => t !== UNTAGGED_TAG);
        for (const t of realTags) query.append("tags", t);
        if (params.tags.includes(UNTAGGED_TAG)) {
            query.set("include_untagged", "true");
        }
    }
    return query;
}

export async function getAiqmSentimentAggregate(
    params: SentimentAggregateParams = {}
): Promise<AiqmSentimentAggregate> {
    if (USE_MOCK) {
        return buildMockAggregate(params.granularity ?? "weekly", params);
    }
    const url = `${apiURL}/training/aiqm/insights/sentiment?${buildSentimentQuery(params).toString()}`;
    const res = await axios.get(url, { headers: authHeaders() });
    return normalizeAggregate(res.data?.data ?? res.data);
}

// Synthesizes the comparison block for MOCK mode only (mock = local fake-data
// generation; there is no BE to read from). Real responses always take the
// comparison straight from the BE — no client-side fallback. Mirrors the BE
// spec: deltas + top movers (themes in both periods, |delta| desc, cap 4).
function computeComparison(
    current: AiqmSentimentAggregate,
    previous: AiqmSentimentAggregate
): SentimentComparison {
    const prevById = new Map<string, SentimentTheme>();
    const prevByLabel = new Map<string, SentimentTheme>();
    for (const t of previous.themes) {
        prevById.set(t.id, t);
        prevByLabel.set(t.label.trim().toLowerCase(), t);
    }
    const movers: SentimentMover[] = [];
    for (const t of current.themes) {
        const match =
            prevById.get(t.id) ?? prevByLabel.get(t.label.trim().toLowerCase());
        if (!match) continue;
        const delta = t.avg_sentiment - match.avg_sentiment;
        if (delta === 0) continue;
        movers.push({
            id: t.id,
            label: t.label,
            avg_sentiment: t.avg_sentiment,
            delta,
        });
    }
    movers.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    return {
        net_sentiment_delta:
            current.overall_net_sentiment - previous.overall_net_sentiment,
        total_calls_delta: current.total_calls - previous.total_calls,
        movers: movers.slice(0, 4),
    };
}

// Parse the BE-provided comparison block; null when the BE omits it (no
// client-side fallback — the FE shows the comparison only when the BE sends it).
function parseComparison(raw: unknown): SentimentComparison | null {
    if (!raw || typeof raw !== "object") return null;
    const c = raw as Record<string, unknown>;
    const num = (v: unknown) =>
        typeof v === "number" && Number.isFinite(v) ? v : 0;
    const movers: SentimentMover[] = Array.isArray(c.movers)
        ? c.movers
              .map((m) => {
                  const mm = (m ?? {}) as Record<string, unknown>;
                  return {
                      id: typeof mm.id === "string" ? mm.id : "",
                      label: typeof mm.label === "string" ? mm.label : "",
                      avg_sentiment: num(mm.avg_sentiment),
                      delta: num(mm.delta),
                  };
              })
              .filter((m) => m.id || m.label)
        : [];
    return {
        net_sentiment_delta: num(c.net_sentiment_delta),
        total_calls_delta: num(c.total_calls_delta),
        movers,
    };
}

// Fetch the current window plus the previous equal-length window for
// period-over-period deltas and biggest movers — in a SINGLE request via
// compare=true (the backend returns { current, previous, comparison }), so a
// tag/filter change costs one round-trip instead of two.
//
// Defensive: if the backend hasn't shipped compare yet it returns the bare
// aggregate, which we treat as `current` with `previous=null` (the comparison
// features simply hide). See BE_HANDOFF_AIQM_INSIGHTS_COMPARE.
export async function getAiqmSentimentComparison(
    params: SentimentAggregateParams = {},
    signal?: AbortSignal
): Promise<SentimentAggregateComparison> {
    if (USE_MOCK) {
        // No network in mock mode — synthesize the previous window from mock data
        // so the comparison UI (deltas, movers) stays demoable from one "call".
        const granularity = params.granularity ?? "weekly";
        const current = buildMockAggregate(granularity, params);
        const prevRange = previousPeriodRange(
            current.start_date || params.start_date || "",
            current.end_date || params.end_date || ""
        );
        const previous = prevRange
            ? buildMockAggregate(granularity, { ...params, ...prevRange })
            : null;
        const comparison = previous ? computeComparison(current, previous) : null;
        return { current, previous, comparison };
    }

    const query = buildSentimentQuery(params);
    query.set("compare", "true");
    const url = `${apiURL}/training/aiqm/insights/sentiment?${query.toString()}`;
    const res = await axios.get(url, { headers: authHeaders(), signal });
    const data = res.data?.data ?? res.data ?? {};

    // compare=true → { current, previous }. Older backends return the bare
    // aggregate object; treat that as current-only (previous hidden).
    const isCompareShape =
        !!data &&
        typeof data === "object" &&
        ("current" in data || "previous" in data);
    const current = normalizeAggregate(isCompareShape ? data.current : data);
    const previous =
        isCompareShape && data.previous != null
            ? normalizeAggregate(data.previous)
            : null;
    // Take the BE-computed comparison as-is (null when the BE omits it).
    const comparison = parseComparison(isCompareShape ? data.comparison : null);
    return { current, previous, comparison };
}

// ───────────────────────────────────────────────────────────────────────────
// Mock data
//
// Deterministic (no Math.random) so demos and snapshots are stable. Generates a
// plausible trend that dips in the middle and recovers, plus a fixed set of
// free-form themes mirroring what an LLM extractor might surface for a support
// call center.
// ───────────────────────────────────────────────────────────────────────────

const MOCK_THEMES: ReadonlyArray<
    Omit<SentimentTheme, "id" | "call_pct" | "sentiment_level">
> = [
    {
        label: "Billing disputes and unexpected charges",
        call_count: 184,
        avg_sentiment: -42,
        breakdown: { positive: 9, neutral: 24, negative: 67 },
        example_call_ids: ["mock-sess-001", "mock-sess-014", "mock-sess-031"],
    },
    {
        label: "Long wait times before reaching an agent",
        call_count: 152,
        avg_sentiment: -35,
        breakdown: { positive: 12, neutral: 28, negative: 60 },
        example_call_ids: ["mock-sess-002", "mock-sess-019"],
    },
    {
        label: "Difficulty cancelling or pausing a subscription",
        call_count: 121,
        avg_sentiment: -28,
        breakdown: { positive: 14, neutral: 33, negative: 53 },
        example_call_ids: ["mock-sess-003", "mock-sess-022", "mock-sess-040"],
    },
    {
        label: "Product setup and onboarding confusion",
        call_count: 98,
        avg_sentiment: -8,
        breakdown: { positive: 26, neutral: 42, negative: 32 },
        example_call_ids: ["mock-sess-004", "mock-sess-027"],
    },
    {
        label: "Delivery and shipping delays",
        call_count: 87,
        avg_sentiment: -18,
        breakdown: { positive: 18, neutral: 35, negative: 47 },
        example_call_ids: ["mock-sess-005"],
    },
    {
        label: "Agent was helpful and resolved the issue quickly",
        call_count: 76,
        avg_sentiment: 64,
        breakdown: { positive: 78, neutral: 16, negative: 6 },
        example_call_ids: ["mock-sess-006", "mock-sess-033", "mock-sess-048"],
    },
    {
        label: "Refund request status and timeline",
        call_count: 64,
        avg_sentiment: -12,
        breakdown: { positive: 21, neutral: 39, negative: 40 },
        example_call_ids: ["mock-sess-007", "mock-sess-029"],
    },
    {
        label: "Praise for friendly and professional service",
        call_count: 51,
        avg_sentiment: 71,
        breakdown: { positive: 84, neutral: 12, negative: 4 },
        example_call_ids: ["mock-sess-008"],
    },
    {
        label: "Account login and password reset trouble",
        call_count: 43,
        avg_sentiment: -5,
        breakdown: { positive: 24, neutral: 47, negative: 29 },
        example_call_ids: ["mock-sess-009", "mock-sess-037"],
    },
    {
        label: "Upsell and plan upgrade questions",
        call_count: 38,
        avg_sentiment: 31,
        breakdown: { positive: 52, neutral: 35, negative: 13 },
        example_call_ids: ["mock-sess-010"],
    },
];

// Number of trend buckets to emit per granularity over a ~90-day default range.
const BUCKETS_BY_GRANULARITY: Record<SentimentGranularity, number> = {
    daily: 90,
    weekly: 13,
    monthly: 3,
};

// Days per bucket, used to space out bucket_start dates deterministically.
const DAYS_PER_BUCKET: Record<SentimentGranularity, number> = {
    daily: 1,
    weekly: 7,
    monthly: 30,
};

function isoDateNDaysBefore(end: Date, days: number): string {
    const d = new Date(end);
    d.setUTCDate(d.getUTCDate() - days);
    return d.toISOString().slice(0, 10);
}

function buildMockAggregate(
    granularity: SentimentGranularity,
    params: SentimentAggregateParams
): AiqmSentimentAggregate {
    const end = params.end_date ? new Date(params.end_date) : new Date();
    const count = BUCKETS_BY_GRANULARITY[granularity];
    const daysPer = DAYS_PER_BUCKET[granularity];

    // Deterministic U-shaped curve: starts mild, dips negative mid-range, recovers.
    const trend: SentimentTrendBucket[] = [];
    for (let i = 0; i < count; i++) {
        const t = count === 1 ? 0 : i / (count - 1); // 0..1
        // Parabola centered at 0.5: positive at edges, negative in the middle.
        const curve = -60 * (1 - Math.pow((t - 0.5) * 2, 2)) + 25;
        const net = Math.round(Math.min(100, Math.max(-100, curve)));
        const daysBack = (count - 1 - i) * daysPer;
        trend.push({
            bucket_start: isoDateNDaysBefore(end, daysBack),
            net_sentiment: net,
            call_count: 40 + ((i * 7) % 35),
        });
    }

    const total_calls = MOCK_THEMES.reduce((sum, th) => sum + th.call_count, 0);
    const themes: SentimentTheme[] = MOCK_THEMES.map((th, i) => ({
        ...th,
        id: `mock-theme-${i + 1}`,
        call_pct:
            total_calls > 0 ? Math.round((th.call_count / total_calls) * 100) : 0,
        sentiment_level: netSentimentToLevel(th.avg_sentiment),
    }));

    // Weight overall sentiment by theme volume.
    const weightedNet =
        themes.reduce((sum, th) => sum + th.avg_sentiment * th.call_count, 0) /
        (total_calls || 1);

    const overall_breakdown = (() => {
        const acc = themes.reduce(
            (a, th) => {
                a.positive += th.breakdown.positive * th.call_count;
                a.neutral += th.breakdown.neutral * th.call_count;
                a.negative += th.breakdown.negative * th.call_count;
                return a;
            },
            { positive: 0, neutral: 0, negative: 0 }
        );
        const denom = total_calls || 1;
        return {
            positive: Math.round(acc.positive / denom),
            neutral: Math.round(acc.neutral / denom),
            negative: Math.round(acc.negative / denom),
        };
    })();

    const overall_net = Math.round(weightedNet);
    return {
        granularity,
        start_date:
            params.start_date ?? isoDateNDaysBefore(end, (count - 1) * daysPer),
        end_date: end.toISOString().slice(0, 10),
        total_calls,
        overall_net_sentiment: overall_net,
        overall_breakdown,
        trend,
        themes,
        net_sentiment_level: netSentimentToLevel(overall_net),
        momentum:
            trend.length >= 2
                ? trend[trend.length - 1].net_sentiment -
                  trend[trend.length - 2].net_sentiment
                : 0,
        theme_split: {
            positive: themes.filter((t) => t.avg_sentiment > 0).length,
            negative: themes.filter((t) => t.avg_sentiment < 0).length,
        },
    };
}

// ───────────────────────────────────────────────────────────────────────────
// Call tags
//
// Tags are free-text labels applied to AIQM calls at upload time, used to group
// calls (e.g. "Billing Q2", "Jakarta team") so the Insights view can be filtered
// by them. BACKEND: not yet implemented — see BE_HANDOFF_AIQM_CALL_TAGS.md.
// ───────────────────────────────────────────────────────────────────────────

const MOCK_TAGS = [
    "Billing Q2",
    "Jakarta team",
    "Escalations",
    "Onboarding calls",
    "VIP customers",
    "Delivery issues",
    "Refund requests",
];

// List the distinct tags that have been used across this client's AIQM calls,
// to power the upload autocomplete and the Insights tag filter.
//
// Real endpoint (when built): GET /training/aiqm/insights/tags
//   - client scope derived server-side from auth
//   - returns a flat array of unique tag strings
export async function listAiqmTags(): Promise<string[]> {
    if (USE_MOCK) {
        return [...MOCK_TAGS];
    }
    try {
        const url = `${apiURL}/training/aiqm/insights/tags`;
        const res = await axios.get(url, { headers: authHeaders() });
        const data = res.data?.data ?? res.data;
        return Array.isArray(data)
            ? data.filter((x): x is string => typeof x === "string")
            : [];
    } catch {
        // Tags are a convenience — never block the view if the list fails.
        return [];
    }
}
